import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {RefreshCw} from 'lucide-react';
import {Button, ProgressRing, useCountdown} from '@chekinapp/ui';
import {useChekinSDK} from 'context/ChekinSDK';
import {useRetryAppLoad} from 'hooks/useRetryAppLoad';
import {FALLBACK_WAIT_SECONDS, HOUR_SECONDS, isLongWait} from './rateWait';

function formatWait(seconds: number) {
  if (seconds >= HOUR_SECONDS) return `${Math.ceil(seconds / HOUR_SECONDS)}h`;

  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

function RateCountdown() {
  const {t} = useTranslation();
  const {throttleWaitSeconds} = useChekinSDK();
  const retryAppLoad = useRetryAppLoad();
  const [announce, setAnnounce] = useState('');

  const waitSeconds = throttleWaitSeconds ?? FALLBACK_WAIT_SECONDS;
  const {timeLeft, resetTimer} = useCountdown({
    initialTime: waitSeconds,
    onFinish: () => setAnnounce(t('error_rate_ready_announce')),
  });
  const isWaiting = timeLeft > 0;

  const handleRetry = () => {
    setAnnounce(t('splash_retry_announce'));
    resetTimer();
    retryAppLoad();
  };

  return (
    <>
      <div className="mt-5 flex w-full max-w-[340px] items-center gap-3.5 rounded-[var(--chekin-radius-lg)] bg-white px-4 py-3.5 text-left">
        <ProgressRing
          value={((waitSeconds - timeLeft) / waitSeconds) * 100}
          size={46}
          strokeWidth={4}
          label={
            <span className="font-mono text-[12.5px] font-bold tabular-nums text-[color:var(--chekin-amber-700)]">
              {formatWait(timeLeft)}
            </span>
          }
          className="[--progress-ring-indicator-from:var(--chekin-amber-500)] [--progress-ring-indicator-to:var(--chekin-amber-500)] [--progress-ring-track:var(--chekin-neutral-100)]"
        />
        <span className="flex min-w-0 flex-col">
          <span className="text-[12.5px] font-bold text-[color:var(--chekin-text-primary)]">
            {isLongWait(throttleWaitSeconds)
              ? t('error_rate_countdown_title_long')
              : t('error_rate_countdown_title')}
          </span>
          <span className="mt-px text-xs leading-[15px] text-[color:var(--chekin-text-secondary)]">
            {t('error_rate_countdown_subtitle')}
          </span>
        </span>
      </div>
      <div className="mt-6 flex w-full max-w-[320px] flex-col gap-2.5">
        <Button variant="primary" disabled={isWaiting} onClick={handleRetry}>
          {t('try_again')}
          <RefreshCw size={16} aria-hidden="true" />
        </Button>
      </div>
      <span className="sr-only" aria-live="polite">
        {announce}
      </span>
    </>
  );
}

export {RateCountdown};
